import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { FileBarChart, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MonthlyStatutoryRegister = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [register, setRegister] = useState(null);
  const [activeTab, setActiveTab] = useState('combined');
  const [deductionReg, setDeductionReg] = useState(null);
  const [employerReg, setEmployerReg] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data); setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);



  const fetchRegister = useCallback(async () => {
    setLoading(true);
    try {
      const [combined, deduction, employer] = await Promise.all([
        axios.get(`${API_URL}/statutory/registers/monthly?company_id=${selectedCompany}&period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/statutory/registers/deduction?company_id=${selectedCompany}&period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/statutory/registers/employer-contribution?company_id=${selectedCompany}&period=${period}`, { withCredentials: true }),
      ]);
      setRegister(combined.data);
      setDeductionReg(deduction.data);
      setEmployerReg(employer.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [period, selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchRegister(); }, [selectedCompany, period, fetchRegister]);

  return (
    <MainLayout>
      <div data-testid="monthly-register-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Monthly Statutory Register</h1>
            <p className="text-sm text-slate-500 mt-1">Consolidated statutory deduction and employer contribution register</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input type="month" data-testid="period-input" value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <button data-testid="refresh-register-btn" onClick={fetchRegister} disabled={loading} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /><span>Refresh</span></button>
            <button data-testid="statutory-export-btn" onClick={() => {
              const data = register?.entries || [];
              if (data.length === 0) { toast.error('No register data to export'); return; }
              downloadCSV(data.map(e => ({
                Employee_Code: e.employee_code, Employee_Name: e.employee_name,
                PF_Employee: e.pf_employee, PF_Employer: e.pf_employer, EPS: e.eps,
                ESI_Employee: e.esi_employee, ESI_Employer: e.esi_employer,
                PT: e.pt, LWF_Employee: e.lwf_employee, LWF_Employer: e.lwf_employer
              })), `statutory_register_${period}.csv`);
              toast.success('Statutory register exported');
            }} className="flex items-center space-x-2 bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-700">
              <Download className="w-4 h-4" /><span>Export CSV</span>
            </button>
          </div>
        </div>

        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
          {[{ key: 'combined', label: 'Combined Register' }, { key: 'deduction', label: 'Deduction Register' }, { key: 'employer', label: 'Employer Contribution' }].map((tab) => (
            <button key={tab.key} data-testid={`tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{tab.label}</button>
          ))}
        </div>

        {activeTab === 'combined' && register && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100"><h2 className="text-base font-semibold text-slate-800">Combined Register - {period} ({register.employee_count} employees)</h2></div>
            <div className="grid grid-cols-5 gap-4 p-4 bg-slate-50 border-b border-slate-100 text-center">
              <div><p className="text-xs text-slate-500">PF (EE+ER)</p><p className="text-lg font-bold text-blue-600">{((register.totals?.pf_employee || 0) + (register.totals?.pf_employer || 0)).toLocaleString()}</p></div>
              <div><p className="text-xs text-slate-500">ESI (EE+ER)</p><p className="text-lg font-bold text-emerald-600">{((register.totals?.esi_employee || 0) + (register.totals?.esi_employer || 0)).toLocaleString()}</p></div>
              <div><p className="text-xs text-slate-500">EPS</p><p className="text-lg font-bold text-indigo-600">{(register.totals?.eps || 0).toLocaleString()}</p></div>
              <div><p className="text-xs text-slate-500">PT</p><p className="text-lg font-bold text-amber-600">{(register.totals?.pt || 0).toLocaleString()}</p></div>
              <div><p className="text-xs text-slate-500">LWF</p><p className="text-lg font-bold text-orange-600">{((register.totals?.lwf_employee || 0) + (register.totals?.lwf_employer || 0)).toLocaleString()}</p></div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-right">Basic</th><th className="px-3 py-2 text-right">Gross</th>
                  <th className="px-3 py-2 text-right">PF EE</th><th className="px-3 py-2 text-right">PF ER</th><th className="px-3 py-2 text-right">EPS</th>
                  <th className="px-3 py-2 text-right">ESI EE</th><th className="px-3 py-2 text-right">ESI ER</th><th className="px-3 py-2 text-right">PT</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {register.register?.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-800">{r.employee_name} <span className="text-slate-400 font-mono">({r.employee_code})</span></td>
                      <td className="px-3 py-2 text-right font-mono">{r.basic?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.gross_wages?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.pf?.employee_pf?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.pf?.employer_pf?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.pf?.eps_amount?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.esi?.employee_esi?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.esi?.employer_esi?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.pt?.amount?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'deduction' && deductionReg && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100"><h2 className="text-base font-semibold text-slate-800">Statutory Deduction Register</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-right">PF</th><th className="px-3 py-2 text-right">ESI</th><th className="px-3 py-2 text-right">PT</th><th className="px-3 py-2 text-right">LWF</th><th className="px-3 py-2 text-right font-semibold">Total</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {deductionReg.register?.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-800">{r.employee_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.pf?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.esi?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.pt?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.lwf?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{r.total?.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-semibold"><td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right font-mono">{deductionReg.totals?.pf?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono">{deductionReg.totals?.esi?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono">{deductionReg.totals?.pt?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono">{deductionReg.totals?.lwf?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono">{deductionReg.totals?.total?.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'employer' && employerReg && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100"><h2 className="text-base font-semibold text-slate-800">Employer Contribution Register</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-right">PF ER</th><th className="px-3 py-2 text-right">EPS</th><th className="px-3 py-2 text-right">EDLI</th><th className="px-3 py-2 text-right">Admin</th><th className="px-3 py-2 text-right">ESI ER</th><th className="px-3 py-2 text-right">LWF ER</th><th className="px-3 py-2 text-right font-semibold">Total</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {employerReg.register?.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-800">{r.employee_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.pf_employer?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.eps?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.edli?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.pf_admin?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.esi_employer?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.lwf_employer?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{r.total?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {(!register || register.employee_count === 0) && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileBarChart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-base font-semibold text-slate-600">No Register Data</h3>
            <p className="text-sm text-slate-400 mt-1">Compute statutory working first to populate the register</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MonthlyStatutoryRegister;
